// SalesPage.jsx — Unified sales dashboard.
// Views: Daily / Weekly / Monthly / Compare-periods / Shifts.
// Pure marketing/operations metrics — NO 59-program metrics.
import React, { useEffect, useMemo, useState } from "react";
import { fm, av, byDay, byWeek, byMonth, getShift } from "./lib/analytics";
import { mkC } from "./lib/chartFactory";
import { Kpi } from "./Dashboard";
import PosReconciliationPanel from "./PosReconciliationPanel";
import DateField from "./components/DateField";
import { ksaAddDays } from "./lib/ksaTime";

const VIEWS = [
  { id: "daily",   label: "📅 يومي" },
  { id: "weekly",  label: "📆 أسبوعي" },
  { id: "monthly", label: "🗓 شهري" },
  { id: "compare", label: "⚖ مقارنة فترتين" },
  { id: "shifts",  label: "⏰ الشفتات" },
];

function fmF(n, d = 1) { return (Number(n) || 0).toFixed(d); }

// ─── CSV helpers ─────────────────────────────────────────────────────────
function escapeCSV(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCSV(filename, headers, rows) {
  const lines = [headers.map(escapeCSV).join(",")];
  rows.forEach((r) => lines.push(r.map(escapeCSV).join(",")));
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ExportBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{
      padding: "6px 12px", fontSize: 12, fontWeight: 700, border: "1px solid var(--gold)",
      background: "transparent", color: "var(--brown-deep)", borderRadius: 8, cursor: "pointer",
    }}>📥 تصدير CSV</button>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function rangeFilter(rows, start, end) {
  if (!start && !end) return rows;
  return rows.filter((r) => {
    const d = (r.date || "").slice(0, 10);
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  });
}

function totals(rows) {
  const total = rows.reduce((s, r) => s + (r.total || 0), 0);
  const disc  = rows.reduce((s, r) => s + (r.disc  || 0), 0);
  const paid  = rows.filter((r) => r.isPaid).length;
  const free  = rows.length - paid;
  const count = rows.length;
  const aov   = paid > 0 ? total / paid : 0;
  return { total, disc, paid, free, count, aov };
}

// ─── Main ────────────────────────────────────────────────────────────────
export default function SalesPage({ fil, dsum }) {
  const [view, setView] = useState("daily");
  const safe = useMemo(() => fil || [], [fil]);
  const dsumSafe = useMemo(() => dsum || [], [dsum]);

  // Filter dsum to match the invoice period range — both must align.
  const dsumInRange = useMemo(() => {
    if (!dsumSafe.length || !safe.length) return [];
    const dates = safe.map((r) => (r.date || "").slice(0, 10)).filter(Boolean).sort();
    if (!dates.length) return [];
    const fr = dates[0], to = dates[dates.length - 1];
    return dsumSafe.filter((r) => {
      const d = (r.date || "").slice(0, 10);
      return d >= fr && d <= to;
    });
  }, [dsumSafe, safe]);

  // Build a unified daily series: prefer POS dsum totals (authoritative) when available,
  // fall back to invoice-derived per-day totals otherwise.
  const dsRaw = useMemo(() => byDay(safe), [safe]);
  const ds = useMemo(() => {
    if (!dsumInRange.length) {
      return dsRaw.map((d) => ({ ...d, source: "inv" }));
    }
    const dsumMap = new Map(dsumInRange.map((r) => [r.date, r]));
    const invMap = new Map(dsRaw.map((d) => [d.date, d]));
    const allDates = Array.from(new Set([...dsumMap.keys(), ...invMap.keys()])).sort();
    return allDates.map((date) => {
      const pos = dsumMap.get(date);
      const inv = invMap.get(date) || { date, total: 0, count: 0, paid: 0, free: 0, disc: 0 };
      if (pos) {
        const total = Number(pos.net_with_tax) || Number(pos.net) || Number(pos.gross) || 0;
        const count = Number(pos.count) || 0;
        const cancels = Number(pos.cancel_count) || 0;
        return {
          date,
          total,
          count,
          paid: Math.max(0, count - cancels),
          free: inv.free || 0, // POS doesn't track free explicitly — keep invoice number
          disc: Number(pos.disc) || inv.disc || 0,
          profit: Number(pos.profit) || 0,
          customers: Number(pos.customers) || 0,
          source: "pos",
        };
      }
      return { ...inv, source: "inv" };
    });
  }, [dsRaw, dsumInRange]);

  // Source-of-truth label for the SalesPage header
  const posCoverage = dsumInRange.length;  // days covered by POS
  const totalDays   = ds.length;           // total days in active period
  const hasPos = posCoverage > 0;

  // Top-of-page hero KPIs — totals derived from the UNIFIED daily series so the
  // numbers respect the active source (POS when available, invoices otherwise).
  // NOTE: Hooks must run before any early return — keep this above the empty-state check.
  const all = useMemo(() => {
    const tot = ds.reduce((s, d) => s + (d.total || 0), 0);
    const cnt = ds.reduce((s, d) => s + (d.count || 0), 0);
    const paid = ds.reduce((s, d) => s + (d.paid || 0), 0);
    return { total: tot, count: cnt, paid, aov: paid > 0 ? tot / paid : 0 };
  }, [ds]);

  if (!safe.length) return <div className="card" data-testid="sales-empty">لا توجد بيانات مبيعات بعد — ارفع ملف فواتير.</div>;

  const lastDay = ds[ds.length - 1];
  const days = ds.length;
  const avgPerDay = days > 0 ? all.total / days : 0;

  return (
    <div data-testid="sales-page">
      {/* Source-of-truth bar — explains where the numbers come from */}
      <div className="sales-src-bar" data-testid="sales-source-bar">
        {hasPos ? (
          <>
            <span className="sales-src-chip sales-src-pos" data-testid="sales-source-pos">
              💰 المبيعات: <b>ملخص المبيعات (POS)</b>
            </span>
            <span className="sales-src-chip">
              📅 تغطية POS: <b>{posCoverage} / {totalDays} يوم</b>
              {posCoverage < totalDays && (
                <span className="sales-src-mini"> · {totalDays - posCoverage} يوم من الفواتير</span>
              )}
            </span>
          </>
        ) : (
          <span className="sales-src-chip sales-src-inv" data-testid="sales-source-inv">
            📊 المبيعات: <b>تقرير الفواتير</b>
            <a className="sales-src-cta" href="#st" onClick={(e) => {
              e.preventDefault();
              // Soft-hint: user knows where to upload. We don't navigate (no router here).
            }}>
              ⓘ ارفع &quot;ملخص المبيعات اليومي&quot; للدقّة الأعلى
            </a>
          </span>
        )}
      </div>

      {/* POS ↔ Invoices reconciliation panel — only renders when both sources exist */}
      <PosReconciliationPanel inv={safe} dsum={dsumSafe} />

      {/* Hero */}
      <div className="card" data-testid="sales-hero" style={{
        marginBottom: 14, padding: "16px 20px",
        background: "linear-gradient(135deg, #6B4E3D 0%, #8B6F58 100%)", color: "#fff", borderRadius: 14,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 1 }}>المبيعات</div>
            <h2 style={{ margin: "2px 0", fontSize: 22, fontWeight: 800 }}>💰 لوحة المبيعات الشاملة</h2>
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              {days} يوم · {fm(all.count)} فاتورة · {fm(all.paid)} مدفوعة
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <HeroTile label="إجمالي" value={`${fm(all.total)} ر`} />
            <HeroTile label="متوسط/يوم" value={`${fm(avgPerDay)} ر`} />
            <HeroTile label="آخر يوم" value={`${fm(lastDay?.total || 0)} ر`} sub={lastDay?.date} />
            <HeroTile label="متوسط الفاتورة (المدفوعة فقط)" value={`${fmF(all.aov, 0)} ر`} sub="من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية" highlight />
          </div>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }} data-testid="sales-view-tabs">
        {VIEWS.map((v) => (
          <button key={v.id} onClick={() => setView(v.id)} data-testid={`sales-view-${v.id}`}
                  style={{
                    padding: "8px 16px", fontSize: 13, fontWeight: 700,
                    background: view === v.id ? "var(--brown)" : "var(--beige)",
                    color: view === v.id ? "#fff" : "var(--brown-deep)",
                    border: "none", borderRadius: 8, cursor: "pointer", transition: "all 160ms ease",
                  }}>
            {v.label}
          </button>
        ))}
      </div>

      {view === "daily"   && <DailyView fil={safe} ds={ds} />}
      {view === "weekly"  && <WeeklyView fil={safe} ds={ds} />}
      {view === "monthly" && <MonthlyView fil={safe} ds={ds} />}
      {view === "compare" && <CompareView fil={safe} ds={ds} />}
      {view === "shifts"  && <ShiftsView fil={safe} dsum={dsumSafe} />}
    </div>
  );
}

function HeroTile({ label, value, sub, highlight }) {
  return (
    <div style={{
      background: highlight ? "rgba(255,255,255,0.20)" : "rgba(255,255,255,0.10)",
      padding: "8px 16px", borderRadius: 10, minWidth: 110,
      border: highlight ? "1.5px solid rgba(255,255,255,0.35)" : "none",
    }}>
      <div style={{ fontSize: 10, opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── DAILY ───────────────────────────────────────────────────────────────
function DailyView({ ds }) {
  const tot = ds.reduce((s, d) => s + d.total, 0);
  const avg = ds.length ? tot / ds.length : 0;
  const best = ds.reduce((a, b) => (b.total > (a?.total || 0) ? b : a), null);
  const worst = ds.filter((d) => d.total > 0).reduce((a, b) => (b.total < (a?.total || Infinity) ? b : a), null);

  // Daily trend chart (last 60 days)
  useEffect(() => {
    const slice = ds.slice(-60);
    mkC("sales-daily-chart", "line", slice.map((d) => d.date), [
      {
        label: "الإيراد اليومي",
        data: slice.map((d) => d.total),
        borderColor: "#6B4E3D",
        backgroundColor: "rgba(194,169,127,0.25)",
        fill: true,
        tension: 0.35,
        pointRadius: 2,
        borderWidth: 2,
      },
      {
        label: "المتوسط",
        data: slice.map(() => avg),
        borderColor: "#C2A97F",
        borderDash: [5, 5],
        pointRadius: 0,
        borderWidth: 1.5,
      },
    ]);
  }, [ds, avg]);

  const handleExport = () => {
    const rows = [...ds].reverse().map((d) => {
      const aov = d.paid > 0 ? d.total / d.paid : 0;
      const vs = avg > 0 ? (d.total / avg - 1) * 100 : 0;
      return [d.date, d.total, d.disc, d.count, d.paid, d.free, aov.toFixed(0), vs.toFixed(1) + "%"];
    });
    downloadCSV("المبيعات-اليومية.csv",
      ["التاريخ", "الإجمالي", "الخصم", "الفواتير", "مدفوعة", "مجاني", "متوسط الفاتورة", "vs المتوسط"], rows);
  };

  return (
    <div data-testid="sales-daily-view">
      <Kpi items={[
        { l: "أيام نشطة", v: fm(ds.length), s: "بمبيعات", c: "" },
        { l: "متوسط اليومي", v: `${fm(avg)} ر`, s: "إجمالي / عدد الأيام", c: "g" },
        { l: "أفضل يوم", v: `${fm(best?.total || 0)} ر`, s: best?.date || "—", c: "g" },
        { l: "أضعف يوم", v: `${fm(worst?.total || 0)} ر`, s: worst?.date || "—", c: "r" },
      ]} />

      {/* Chart */}
      <div className="card" style={{ marginTop: 14, padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 10 }}>
          📈 اتجاه الإيراد اليومي (آخر {Math.min(60, ds.length)} يوم)
        </div>
        <div style={{ height: 280 }}>
          <canvas id="sales-daily-chart" data-testid="daily-chart" />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 14 }}>
        <div style={{ padding: "10px 14px", background: "var(--beige)", borderBottom: "1px solid var(--border)",
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "var(--brown-deep)" }}>📊 المبيعات اليومية ({ds.length} يوم)</span>
          <ExportBtn onClick={handleExport} testid="export-daily-csv" />
        </div>
        <div style={{ maxHeight: 460, overflowY: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead style={{ background: "var(--beige)", position: "sticky", top: 0 }}>
              <tr>
                <th style={th}>التاريخ</th>
                <th style={thC}>الإجمالي</th>
                <th style={thC}>الفواتير</th>
                <th style={thC}>مدفوعة</th>
                <th style={thC}>مجاني</th>
                <th style={thC}>متوسط الفاتورة (المدفوعة فقط)<div style={{ fontSize: 10, fontWeight: 400 }}>من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية</div></th>
                <th style={thC}>vs المتوسط</th>
              </tr>
            </thead>
            <tbody>
              {[...ds].reverse().map((d) => {
                const aov = d.paid > 0 ? d.total / d.paid : 0;
                const vs = avg > 0 ? (d.total / avg - 1) * 100 : 0;
                return (
                  <tr key={d.date} data-testid={`daily-row-${d.date}`} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={td}><b>{d.date}</b></td>
                    <td style={tdC}><b style={{ color: "var(--brown-deep)" }}>{fm(d.total)} ر</b></td>
                    <td style={tdC}>{d.count}</td>
                    <td style={tdC}>{d.paid}</td>
                    <td style={tdC}><span style={{ color: "var(--muted)" }}>{d.free}</span></td>
                    <td style={tdC}>{fmF(aov, 0)} ر</td>
                    <td style={tdC}>
                      <span style={{ color: vs >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>
                        {vs >= 0 ? "▲" : "▼"} {Math.abs(vs).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── WEEKLY ──────────────────────────────────────────────────────────────
function WeeklyView({ ds }) {
  const wks = useMemo(() => byWeek(ds), [ds]);
  const tot = wks.reduce((s, w) => s + w.total, 0);
  const avg = wks.length ? tot / wks.length : 0;

  useEffect(() => {
    mkC("sales-weekly-chart", "bar", wks.map((w) => w.week), [
      {
        label: "إيراد الأسبوع",
        data: wks.map((w) => w.total),
        backgroundColor: wks.map((w) => w.total >= avg ? "rgba(16,185,129,0.75)" : "rgba(217,119,6,0.75)"),
        borderRadius: 6,
        borderSkipped: false,
      },
    ]);
  }, [wks, avg]);

  const handleExport = () => {
    const rows = wks.map((w, i) => {
      const prev = wks[i - 1];
      const vs = prev && prev.total > 0 ? ((w.total - prev.total) / prev.total * 100) : 0;
      return [w.week, w.total, w.count, (w.total / 7).toFixed(0), vs.toFixed(1) + "%"];
    });
    downloadCSV("المبيعات-الأسبوعية.csv",
      ["الأسبوع", "الإجمالي", "الفواتير", "متوسط/يوم", "vs السابق"], rows);
  };

  return (
    <div data-testid="sales-weekly-view">
      <Kpi items={[
        { l: "عدد الأسابيع", v: fm(wks.length), s: "بمبيعات", c: "" },
        { l: "متوسط أسبوعي", v: `${fm(avg)} ر`, s: "إجمالي / عدد الأسابيع", c: "g" },
        { l: "إجمالي الفترة", v: `${fm(tot)} ر`, s: `${wks.length} أسبوع`, c: "g" },
      ]} />

      <div className="card" style={{ marginTop: 14, padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 10 }}>
          📊 الإيراد الأسبوعي (أخضر = فوق المتوسط · برتقالي = تحت)
        </div>
        <div style={{ height: 260 }}>
          <canvas id="sales-weekly-chart" data-testid="weekly-chart" />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 14 }}>
        <div style={{ padding: "10px 14px", background: "var(--beige)", display: "flex",
                      justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "var(--brown-deep)" }}>📆 تفصيل الأسابيع</span>
          <ExportBtn onClick={handleExport} testid="export-weekly-csv" />
        </div>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--beige)" }}>
            <tr><th style={th}>الأسبوع</th><th style={thC}>الإجمالي</th><th style={thC}>الفواتير</th><th style={thC}>متوسط/يوم</th><th style={thC}>vs السابق</th></tr>
          </thead>
          <tbody>
            {wks.map((w, i) => {
              const prev = wks[i - 1];
              const vs = prev && prev.total > 0 ? ((w.total - prev.total) / prev.total * 100) : 0;
              return (
                <tr key={w.week} data-testid={`weekly-row-${w.week}`} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}><b>{w.week}</b></td>
                  <td style={tdC}><b style={{ color: "var(--brown-deep)" }}>{fm(w.total)} ر</b></td>
                  <td style={tdC}>{w.count}</td>
                  <td style={tdC}>{fm(w.total / 7)} ر</td>
                  <td style={tdC}>
                    {prev ? <span style={{ color: vs >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>{vs >= 0 ? "▲" : "▼"} {Math.abs(vs).toFixed(0)}%</span> : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MONTHLY ─────────────────────────────────────────────────────────────
function MonthlyView({ ds }) {
  // Roll up the unified daily series into months so we honour the POS-or-Invoice source choice.
  const months = useMemo(() => {
    const m = {};
    for (const d of ds || []) {
      const ym = (d.date || "").slice(0, 7);
      if (!ym) continue;
      const e = m[ym] || (m[ym] = { month: ym, total: 0, count: 0, paid: 0, free: 0, disc: 0 });
      e.total += d.total || 0;
      e.count += d.count || 0;
      e.paid  += d.paid  || 0;
      e.free  += d.free  || 0;
      e.disc  += d.disc  || 0;
    }
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month));
  }, [ds]);
  const tot = months.reduce((s, m) => s + m.total, 0);
  const avg = months.length ? tot / months.length : 0;

  useEffect(() => {
    mkC("sales-monthly-chart", "bar", months.map((m) => m.month), [
      {
        label: "إيراد الشهر",
        data: months.map((m) => m.total),
        backgroundColor: "rgba(107,78,61,0.85)",
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        type: "line",
        label: "متوسط الفاتورة (المدفوعة فقط)",
        data: months.map((m) => m.paid > 0 ? m.total / m.paid : 0),
        borderColor: "#C2A97F",
        backgroundColor: "transparent",
        yAxisID: "y2",
        tension: 0.3,
        pointRadius: 3,
        borderWidth: 2,
      },
    ], {
      scales: {
        y: { position: "right", title: { display: true, text: "الإيراد" } },
        y2: { position: "left", grid: { drawOnChartArea: false }, title: { display: true, text: "متوسط الفاتورة (المدفوعة فقط)" } },
      },
    });
  }, [months]);

  const handleExport = () => {
    const rows = months.map((m, i) => {
      const prev = months[i - 1];
      const vs = prev && prev.total > 0 ? ((m.total - prev.total) / prev.total * 100) : 0;
      const aov = m.paid > 0 ? m.total / m.paid : 0;
      return [m.month, m.total, m.count, m.paid, aov.toFixed(0), vs.toFixed(1) + "%"];
    });
    downloadCSV("المبيعات-الشهرية.csv",
      ["الشهر", "الإجمالي", "الفواتير", "مدفوعة", "متوسط الفاتورة", "vs السابق"], rows);
  };

  return (
    <div data-testid="sales-monthly-view">
      <Kpi items={[
        { l: "عدد الأشهر", v: fm(months.length), s: "بمبيعات", c: "" },
        { l: "متوسط شهري", v: `${fm(avg)} ر`, c: "g" },
        { l: "إجمالي", v: `${fm(tot)} ر`, c: "g" },
      ]} />

      <div className="card" style={{ marginTop: 14, padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 10 }}>
          📊 الإيراد الشهري + متوسط الفاتورة
        </div>
        <div style={{ height: 300 }}>
          <canvas id="sales-monthly-chart" data-testid="monthly-chart" />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 14 }}>
        <div style={{ padding: "10px 14px", background: "var(--beige)", display: "flex",
                      justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "var(--brown-deep)" }}>🗓 تفصيل الأشهر</span>
          <ExportBtn onClick={handleExport} testid="export-monthly-csv" />
        </div>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--beige)" }}>
            <tr><th style={th}>الشهر</th><th style={thC}>الإجمالي</th><th style={thC}>الفواتير</th><th style={thC}>مدفوعة</th><th style={thC}>متوسط الفاتورة (المدفوعة فقط)<div style={{ fontSize: 10, fontWeight: 400 }}>من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية</div></th><th style={thC}>vs السابق</th></tr>
          </thead>
          <tbody>
            {months.map((m, i) => {
              const prev = months[i - 1];
              const vs = prev && prev.total > 0 ? ((m.total - prev.total) / prev.total * 100) : 0;
              const aov = m.paid > 0 ? m.total / m.paid : 0;
              return (
                <tr key={m.month} data-testid={`monthly-row-${m.month}`} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}><b>{m.month}</b></td>
                  <td style={tdC}><b style={{ color: "var(--brown-deep)" }}>{fm(m.total)} ر</b></td>
                  <td style={tdC}>{m.count}</td>
                  <td style={tdC}>{m.paid}</td>
                  <td style={tdC}>{fmF(aov, 0)} ر</td>
                  <td style={tdC}>
                    {prev ? <span style={{ color: vs >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>{vs >= 0 ? "▲" : "▼"} {Math.abs(vs).toFixed(0)}%</span> : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── COMPARE ─────────────────────────────────────────────────────────────
function CompareView({ fil, ds }) {
  // Default: last 30 days vs prior 30 days
  const dates = [...new Set((ds || []).map((r) => (r.date || "").slice(0, 10)).filter(Boolean))].sort();
  const lastDate = dates[dates.length - 1];
  const offsetDate = (iso, n) => {
    if (!iso) return "";
    return ksaAddDays(String(iso).slice(0, 10), n);
  };
  const [a, setA] = useState({ start: offsetDate(lastDate, -29), end: lastDate || "" });
  const [b, setB] = useState({ start: offsetDate(lastDate, -59), end: offsetDate(lastDate, -30) });

  // Aggregate the unified daily series between [start, end] inclusive.
  const aggDs = (start, end) => {
    const rows = (ds || []).filter((d) => {
      const dt = (d.date || "").slice(0, 10);
      return dt >= start && dt <= end;
    });
    const tot = rows.reduce((s, d) => s + (d.total || 0), 0);
    const cnt = rows.reduce((s, d) => s + (d.count || 0), 0);
    const paid = rows.reduce((s, d) => s + (d.paid || 0), 0);
    const free = rows.reduce((s, d) => s + (d.free || 0), 0);
    const disc = rows.reduce((s, d) => s + (d.disc || 0), 0);
    return { total: tot, count: cnt, paid, free, disc, aov: paid > 0 ? tot / paid : 0, days: rows.length };
  };
  const aT = useMemo(() => aggDs(a.start, a.end), [ds, a]);  // eslint-disable-line react-hooks/exhaustive-deps
  const bT = useMemo(() => aggDs(b.start, b.end), [ds, b]);  // eslint-disable-line react-hooks/exhaustive-deps
  const aRows = { length: aT.days };
  const bRows = { length: bT.days };

  const delta = (x, y) => y > 0 ? ((x - y) / y * 100) : (x > 0 ? 100 : 0);

  // Comparison bar chart
  useEffect(() => {
    mkC("sales-compare-chart", "bar",
      ["الإيراد", "الفواتير", "مدفوعة", "متوسط الفاتورة"],
      [
        { label: `🅰 ${a.start} → ${a.end}`, data: [aT.total, aT.count, aT.paid, aT.aov], backgroundColor: "rgba(99,102,241,0.8)", borderRadius: 6 },
        { label: `🅱 ${b.start} → ${b.end}`, data: [bT.total, bT.count, bT.paid, bT.aov], backgroundColor: "rgba(236,72,153,0.8)", borderRadius: 6 },
      ]
    );
  }, [a, b, aT.total, aT.count, aT.paid, aT.aov, bT.total, bT.count, bT.paid, bT.aov]);

  const handleExport = () => {
    const rows = [
      ["الإيراد", aT.total, bT.total],
      ["عدد الفواتير", aT.count, bT.count],
      ["مدفوعة", aT.paid, bT.paid],
      ["مجاني", aT.free, bT.free],
      ["الخصومات", aT.disc, bT.disc],
      ["متوسط الفاتورة", aT.aov.toFixed(0), bT.aov.toFixed(0)],
      ["متوسط/يوم", aRows.length ? (aT.total / aRows.length).toFixed(0) : 0, bRows.length ? (bT.total / bRows.length).toFixed(0) : 0],
    ];
    downloadCSV("مقارنة-الفترات.csv",
      ["المؤشر", `🅰 ${a.start} → ${a.end}`, `🅱 ${b.start} → ${b.end}`], rows);
  };

  return (
    <div data-testid="sales-compare-view">
      <div className="card" style={{ marginBottom: 14, padding: 14 }}>
        <div className="g2" style={{ gap: 14 }}>
          <PeriodPicker label="🅰 الفترة الأولى" start={a.start} end={a.end} onChange={setA} testid="period-a" />
          <PeriodPicker label="🅱 الفترة الثانية" start={b.start} end={b.end} onChange={setB} testid="period-b" />
        </div>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 10 }}>
          📊 مقارنة بصرية
        </div>
        <div style={{ height: 260 }}>
          <canvas id="sales-compare-chart" data-testid="compare-chart" />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="compare-table">
        <div style={{ padding: "10px 14px", background: "var(--beige)", display: "flex",
                      justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "var(--brown-deep)" }}>⚖ جدول المقارنة</span>
          <ExportBtn onClick={handleExport} testid="export-compare-csv" />
        </div>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--beige)" }}>
            <tr>
              <th style={th}>المؤشر</th>
              <th style={thC}>🅰 {a.start} → {a.end}</th>
              <th style={thC}>🅱 {b.start} → {b.end}</th>
              <th style={thC}>الفرق</th>
              <th style={thC}>%</th>
            </tr>
          </thead>
          <tbody>
            <CompareRow label="💰 الإيراد" aVal={aT.total} bVal={bT.total} suffix=" ر" />
            <CompareRow label="📄 عدد الفواتير" aVal={aT.count} bVal={bT.count} />
            <CompareRow label="✅ مدفوعة" aVal={aT.paid} bVal={bT.paid} />
            <CompareRow label="🎁 مجانية" aVal={aT.free} bVal={bT.free} inverse />
            <CompareRow label="💸 الخصومات" aVal={aT.disc} bVal={bT.disc} suffix=" ر" inverse />
            <CompareRow label="🧾 متوسط الفاتورة (المدفوعة فقط · من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية)" aVal={aT.aov} bVal={bT.aov} suffix=" ر" decimals={0} />
            <CompareRow label="📅 متوسط/يوم" aVal={aRows.length ? aT.total / aRows.length : 0} bVal={bRows.length ? bT.total / bRows.length : 0} suffix=" ر" decimals={0} />
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: 14, padding: 14, background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(236,72,153,0.04) 100%)", borderInlineStart: "3px solid #6366F1" }}>
        <b style={{ color: "var(--brown-deep)" }}>📊 الخلاصة:</b>
        <div style={{ fontSize: 12.5, color: "var(--brown-deep)", marginTop: 6, lineHeight: 1.9 }}>
          الفترة 🅰 حقّقت {fm(aT.total)} ر مقابل {fm(bT.total)} ر في الفترة 🅱 —
          <b style={{ color: delta(aT.total, bT.total) >= 0 ? "var(--success)" : "var(--danger)" }}>
            {" "}{delta(aT.total, bT.total) >= 0 ? "نمو" : "تراجع"} بنسبة {Math.abs(delta(aT.total, bT.total)).toFixed(1)}%
          </b>.
          {aT.aov !== bT.aov && (
            <> متوسط الفاتورة {aT.aov > bT.aov ? "ارتفع" : "انخفض"} من {fmF(bT.aov, 0)} إلى {fmF(aT.aov, 0)} ر.</>
          )}
        </div>
      </div>
    </div>
  );
}

function PeriodPicker({ label, start, end, onChange, testid }) {
  return (
    <div style={{ padding: 10, background: "var(--beige)", borderRadius: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <DateField  value={start} onChange={(e) => onChange({ start: e.target.value, end })} data-testid={`${testid}-start`}
               style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)", fontSize: 12 }} />
        <span style={{ color: "var(--muted)" }}>→</span>
        <DateField  value={end} onChange={(e) => onChange({ start, end: e.target.value })} data-testid={`${testid}-end`}
               style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)", fontSize: 12 }} />
      </div>
    </div>
  );
}

function CompareRow({ label, aVal, bVal, suffix = "", decimals = 0, inverse = false }) {
  const diff = aVal - bVal;
  const pct = bVal > 0 ? (diff / bVal * 100) : (aVal > 0 ? 100 : 0);
  const isGood = inverse ? diff <= 0 : diff >= 0;
  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={td}>{label}</td>
      <td style={tdC}><b>{fmF(aVal, decimals)}{suffix}</b></td>
      <td style={tdC}>{fmF(bVal, decimals)}{suffix}</td>
      <td style={tdC}>
        <span style={{ color: isGood ? "var(--success)" : "var(--danger)" }}>
          {diff >= 0 ? "+" : ""}{fmF(diff, decimals)}{suffix}
        </span>
      </td>
      <td style={tdC}>
        <span style={{ color: isGood ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>
          {pct >= 0 ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
        </span>
      </td>
    </tr>
  );
}

// ─── SHIFTS ──────────────────────────────────────────────────────────────
function ShiftsView({ fil, dsum }) {
  const SHIFT_META = {
    S1: { label: "صباحي (7ص - 3م)",   icon: "☀", color: "#F59E0B" },
    S2: { label: "مسائي (3م - 11م)",  icon: "🌆", color: "#EF4444" },
    S3: { label: "ليلي (11م - 7ص)",   icon: "🌙", color: "#6366F1" },
  };

  // Step 1 — aggregate INVOICES per shift (using their time field for accurate hour).
  // Invoices give us: time distribution + customer-registration ratio.
  // We intentionally count phone presence per shift to compute "نسبة تسجيل العملاء".
  const invAgg = useMemo(() => {
    const m = { S1: mkEmpty(), S2: mkEmpty(), S3: mkEmpty() };
    function mkEmpty() {
      return { total: 0, count: 0, paid: 0, free: 0, disc: 0, withPhone: 0, daySet: new Set() };
    }
    for (const r of fil) {
      // r.hour is the canonical pre-parsed integer (set during upload from
      // the invoice's time column). Fall back to parsing r.time/r.raw only
      // if hour wasn't captured.
      let h;
      if (typeof r.hour === "number" && !isNaN(r.hour)) {
        h = r.hour;
      } else if (r.time) {
        const parts = String(r.time).split(":");
        h = Number(parts[0]);
        if (isNaN(h)) h = 12;
      } else if (r.raw) {
        const m = String(r.raw).match(/(\d{1,2}):\d{2}/);
        h = m ? parseInt(m[1], 10) : 12;
      } else {
        h = 12;
      }
      const s = getShift(h);
      const entry = m[s] || m.S1;
      entry.total += r.total || 0;
      entry.disc  += r.disc  || 0;
      entry.count += 1;
      if (r.isPaid) entry.paid += 1; else entry.free += 1;
      // Registered customer = phone string of >=7 chars and not a "0" placeholder.
      // (POS exports use "0" for walk-ins.)
      const ph = String(r.phone || "").replace(/[^\d]/g, "");
      if (ph.length >= 7 && Number(ph) > 0) entry.withPhone += 1;
      if (r.date) entry.daySet.add(r.date);
    }
    return m;
  }, [fil]);

  // Step 2 — date range of the active invoice filter ⇒ use it to clip dsum to the same window.
  const dsumInRange = useMemo(() => {
    if (!dsum || !dsum.length || !fil.length) return [];
    const dates = fil.map((r) => (r.date || "").slice(0, 10)).filter(Boolean).sort();
    if (!dates.length) return [];
    const fr = dates[0], to = dates[dates.length - 1];
    return dsum.filter((r) => {
      const d = (r.date || "").slice(0, 10);
      return d >= fr && d <= to;
    });
  }, [dsum, fil]);

  // POS total (authoritative) over the active range.
  const dsumTotal = useMemo(
    () => dsumInRange.reduce((s, r) => s + (Number(r.net_with_tax) || Number(r.net) || Number(r.total) || 0), 0),
    [dsumInRange],
  );
  const usePos = dsumInRange.length > 0 && dsumTotal > 0;

  // Step 3 — hybrid per-shift figures: scale POS daily totals by invoice-derived shift share.
  // If POS data isn't available, fall back to raw invoice totals.
  const invGrand = invAgg.S1.total + invAgg.S2.total + invAgg.S3.total;
  const agg = useMemo(() => {
    const out = {};
    for (const k of ["S1", "S2", "S3"]) {
      const inv = invAgg[k];
      const share = invGrand > 0 ? inv.total / invGrand : 0;
      const total = usePos ? dsumTotal * share : inv.total;
      const regPct = inv.count > 0 ? (inv.withPhone / inv.count) * 100 : 0;
      out[k] = { ...inv, share, total, regPct };
    }
    return out;
  }, [invAgg, invGrand, usePos, dsumTotal]);

  const grand = ["S1", "S2", "S3"].reduce((s, k) => s + agg[k].total, 0);

  // Time-data warning: only show if NONE of the rows have an hour.
  const noTimeData = fil.length > 0 && fil.every((r) => typeof r.hour !== "number" || isNaN(r.hour));

  useEffect(() => {
    mkC("sales-shifts-chart", "doughnut",
      ["S1", "S2", "S3"].map((k) => SHIFT_META[k].label),
      [{
        data: ["S1", "S2", "S3"].map((k) => agg[k].total),
        backgroundColor: ["S1", "S2", "S3"].map((k) => SHIFT_META[k].color),
        borderWidth: 2,
        borderColor: "#fff",
      }]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agg, grand]);

  const handleExport = () => {
    const rows = ["S1", "S2", "S3"].map((k) => {
      const a = agg[k];
      const share = grand > 0 ? a.total / grand * 100 : 0;
      const aov = a.paid > 0 ? a.total / a.paid : 0;
      const days = a.daySet.size;
      return [
        SHIFT_META[k].label,
        a.total.toFixed(0),
        share.toFixed(1) + "%",
        a.count,
        a.paid,
        a.free,
        aov.toFixed(0),
        days,
        days > 0 ? (a.total / days).toFixed(0) : 0,
        a.withPhone,
        a.regPct.toFixed(1) + "%",
      ];
    });
    downloadCSV("الشفتات.csv",
      ["الشفت", "الإجمالي", "حصة %", "الفواتير", "مدفوعة", "مجاني", "متوسط الفاتورة", "عدد الأيام", "متوسط/يوم", "عملاء مسجّلين", "نسبة التسجيل"],
      rows,
    );
  };

  return (
    <div data-testid="sales-shifts-view">
      {/* Source-of-truth indicator */}
      <div className="shifts-src-bar" data-testid="shifts-source">
        <span className="shifts-src-chip shifts-src-amount">
          💰 المبيعات: {usePos ? <b>ملخص المبيعات (POS)</b> : <b>تقرير الفواتير</b>}
        </span>
        <span className="shifts-src-chip shifts-src-time">
          ⏰ وقت الشفت: <b>تقرير الفواتير</b>
        </span>
        {usePos && (
          <span className="shifts-src-chip shifts-src-note">
            ⓘ التوزيع: ساعات الفواتير × إجمالي POS
          </span>
        )}
      </div>

      {noTimeData && (
        <div className="al alo" data-testid="shifts-no-time-warning" style={{ marginBottom: 14, padding: 14, lineHeight: 1.9 }}>
          <b>⚠ تنبيه:</b> ملف الفواتير لا يحتوي على عمود <b>وقت البيع</b>، لذلك توزيع الشفتات أدناه <b>غير دقيق</b>.
          <br />
          📥 <b>الحل:</b> ارفع <b>تقرير الفواتير التفصيلي</b> من نظام POS (يحتوي على عمود &quot;وقت البيع&quot; أو &quot;الوقت&quot;).
        </div>
      )}
      <div className="g3" style={{ gap: 14 }}>
        {["S1", "S2", "S3"].map((k) => {
          const a = agg[k];
          const meta = SHIFT_META[k];
          const share = grand > 0 ? a.total / grand * 100 : 0;
          const aov = a.paid > 0 ? a.total / a.paid : 0;
          const days = a.daySet.size;
          return (
            <div key={k} className="card" data-testid={`shift-card-${k}`} style={{
              padding: 16, borderInlineStart: `4px solid ${meta.color}`,
              background: `linear-gradient(135deg, ${meta.color}0A 0%, transparent 100%)`,
            }}>
              <div style={{ fontSize: 13, color: meta.color, fontWeight: 700, marginBottom: 4 }}>
                {meta.icon} {meta.label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "var(--brown-deep)" }}>{fm(a.total)} ر</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                {fmF(share, 1)}% من إجمالي المبيعات
              </div>
              <div style={{ height: 6, background: "var(--beige)", borderRadius: 6, marginTop: 8, overflow: "hidden" }}>
                <div style={{ width: share + "%", height: "100%", background: meta.color }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12, fontSize: 12 }}>
                <MiniStat label="فواتير" value={fm(a.count)} />
                <MiniStat label="مدفوعة" value={fm(a.paid)} color="var(--success)" />
                <MiniStat label="مجاني" value={fm(a.free)} color="var(--muted)" />
                <MiniStat label="متوسط الفاتورة (المدفوعة فقط · من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية)" value={`${fmF(aov, 0)} ر`} color="var(--brown-deep)" />
                <MiniStat label="عدد الأيام" value={fm(days)} />
                <MiniStat label="متوسط/يوم" value={`${fm(days > 0 ? a.total / days : 0)} ر`} color="var(--success)" />
              </div>

              {/* Registration % bar */}
              <div className="shift-reg" data-testid={`shift-reg-${k}`}>
                <div className="shift-reg-head">
                  <span>📇 نسبة تسجيل العملاء</span>
                  <span className="shift-reg-val" style={{ color: meta.color }}>{fmF(a.regPct, 1)}%</span>
                </div>
                <div className="shift-reg-bar">
                  <div className="shift-reg-fill" style={{ width: `${Math.min(100, a.regPct)}%`, background: meta.color }} />
                </div>
                <div className="shift-reg-sub">
                  {fm(a.withPhone)} عميل مسجّل من أصل {fm(a.count)} فاتورة
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="g2" style={{ gap: 14, marginTop: 14 }}>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 10,
                        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>📊 توزيع المبيعات بين الشفتات</span>
            <ExportBtn onClick={handleExport} testid="export-shifts-csv" />
          </div>
          <div style={{ height: 240 }}>
            <canvas id="sales-shifts-chart" data-testid="shifts-chart" />
          </div>
        </div>

        <div className="card" style={{ padding: 14, background: "var(--beige)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 10 }}>
            💡 تحليل سريع
          </div>
          <div style={{ fontSize: 12.5, color: "var(--brown-deep)", lineHeight: 1.9 }}>
            الشفت الأفضل هو <b style={{ color: "var(--success)" }}>
            {(() => {
              const best = ["S1", "S2", "S3"].reduce((a, k) => agg[k].total > agg[a].total ? k : a, "S1");
              return `${SHIFT_META[best].icon} ${SHIFT_META[best].label} (${fmF(agg[best].total / Math.max(grand, 1) * 100, 0)}%)`;
            })()}</b>.
            <br />
            {(() => {
              const bestReg = ["S1", "S2", "S3"].reduce((a, k) => agg[k].regPct > agg[a].regPct ? k : a, "S1");
              return <>أعلى تسجيل عملاء في الشفت <b style={{ color: SHIFT_META[bestReg].color }}>
                {SHIFT_META[bestReg].icon} {SHIFT_META[bestReg].label}</b> بنسبة <b>{fmF(agg[bestReg].regPct, 1)}%</b>.</>;
            })()}
            <br />
            ركّز موظفيك الأقوى على الشفت الأقوى. الشفت ذو نسبة التسجيل المنخفضة يحتاج تدريب الكاشير على طلب رقم العميل.
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color = "var(--brown-deep)" }) {
  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--muted)" }}>{label}</div>
    </div>
  );
}

// ─── Shared table styles ─────────────────────────────────────────────────
const th  = { padding: "10px 14px", textAlign: "start", fontSize: 11.5, color: "var(--muted)", fontWeight: 700 };
const thC = { padding: "10px 8px",  textAlign: "center", fontSize: 11.5, color: "var(--muted)", fontWeight: 700 };
const td  = { padding: "10px 14px" };
const tdC = { padding: "10px 8px",  textAlign: "center" };

// Unused import suppressor (kept for backward compatibility)
void av;
